import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FPCategoryVM, GroupVM } from "@models/backend-generated-models/models";
import * as moment from 'moment';
import { environment } from '@environment/environment';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { UserMenuService } from '@api/user-menu.service';
import { take } from 'rxjs/operators';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ResourceSeriesChartComponent } from './_components/resource-series-chart/resource-series-chart.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { endOfPeriod, nextPeriodStart, Period, startOfPeriod, stamp, subtractPeriods } from './_components/resource-series-chart/periods';

/**
 * How this dialog's date fields read what is typed into them: strictly, as one of three shapes, with
 * a four-digit year.
 *
 * The app-wide format parses "D-MMM-yyyy", and moment reads "yyyy" loosely as an era year, so
 * "1-Feb-26" was the year 26, "01/02/2026" the year 2 and "Feb 1 2026" 1 January. Half-typed dates
 * reached the API as the year 202 (TLI Group, 12/10/26). Strict parsing refuses anything that is not
 * a whole date, and the field keeps the last one it had.
 */
const REPORT_DATE_FORMATS = {
  parse: { dateInput: ['D-MMM-YYYY', 'D MMM YYYY', 'D/M/YYYY', 'YYYY-MM-DD'] },
  display: { dateInput: 'D-MMM-YYYY', monthYearLabel: 'MMM YYYY', dateA11yLabel: 'LL', monthYearA11yLabel: 'MMMM YYYY' }
};

@Component({
  selector: 'app-modal-report-base',
  templateUrl: './modal-reports.component.html',
  styleUrls: ['./modal-reports.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: REPORT_DATE_FORMATS }
  ]
})
export class ModalReportsComponent implements OnInit, OnDestroy {

  url: string = "";
  aPeriodList: string[] = [];
  selectedPeriod: string | undefined = "";
  errorMessage: string = "";

  chartTypeList: IChart[] = [];
  chartType: IChart = {} as IChart;

  startDate?: Date;
  startDateForIframe?: Date;

  endDate?: Date;
  endDateForIframe?: Date;

  isUrlSafeLoad: boolean = true;
  isShowDatepicker: boolean = false;
  isShowCompare: boolean = false;
  isCompare: boolean = false;
  isToolbarActive: boolean = false;
  isShowLabel: boolean = true;
  isShowGroups: boolean = false;

  selectedGroup: GroupVM = {} as GroupVM;

  periodListType = { "D": "Day", "W": "Week", "M": "Month", "Q": "Quarter", "Y": "Year" };

  isLoading$ = new BehaviorSubject<boolean>(true);
  isResourceRemainderConsumption$ = new BehaviorSubject<boolean>(false);
  isUrlsReturnLength = new BehaviorSubject<boolean>(false);

  current_url: SafeUrl | undefined;
  isCarbonReport: boolean = false;

  /**
   * The native chart, for its what-if template. The panel is declared there and rendered here, in
   * its own column beside the whole dialog, so it is not confined to the chart's height.
   */
  @ViewChild(ResourceSeriesChartComponent) chartCmp?: ResourceSeriesChartComponent;

  /**
   * The docked panel lives in THIS view, so the chart marking its own detector dirty does not
   * reach it. It tells us instead, and we mark ours.
   */
  onChartStateChanged(): void {
    this.cdr.markForCheck();
  }

  constructor(
    private dialogRef: MatDialogRef<ModalReportsComponent>,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private userMenuService: UserMenuService,
    @Inject(MAT_DIALOG_DATA) public data: { category: FPCategoryVM, url: Observable<string> }
  ) { }

  ngOnInit(): void {

    this.data.url.pipe(take(1)).subscribe(url => {
      this.url = url;
      this.loadingReport();
    });
  }

  async loadingReport() {
    this.isUrlSafeLoad = true;
    this.errorMessage = "";

    if (Array.isArray(this.data.category.groups) && this.data.category.groups.length > 0) {
      this.selectedGroup = this.data.category.groups[0];
    } else {
      this.selectedGroup = {} as GroupVM;
    }

    this.createPeriodList();
    this.createChartTypeList();
    this.createCompare();
    this.createDefaultData();
    this.openOnLatestReading();
    // A Compare category, or a classic one, skips the lookup above and opens on its defaults. Those
    // have to reach the native chart now: it only reads dates once they are committed, and a
    // category opening in Compare used to sit blank until something was changed.
    if (this.nativeWindowReady) this.commitNativeDates();

    const params = this.createReportParams();

    const missing = this.validateRequiredParams(params);
    if (missing.length) {
      this.errorMessage = `Report cannot load. Missing: ${missing.join(", ")}`;
      console.error("Missing required parameters:", missing);
      return;
    }

    const fullUrl = this.buildReportUrl(params);
    if (!fullUrl) {
      this.errorMessage = "Report URL could not be generated.";
      return;
    }

    this.isUrlSafeLoad = false;
    this.current_url = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
    setTimeout(() => {
      this.isLoading$.next(false);
    }, 100);

    const reportUrl = (this.data.category?.report?.reportUrl || "").toLowerCase();
    this.isCarbonReport = reportUrl.includes("rptresourcepolicy");
    this.setupResourceReportListenersIfNeeded();
  }

  private updateReportUrl() {
    this.commitNativeDates();
    this.isLoading$.next(true);
    this.isUrlSafeLoad = true;

    const params = this.createReportParams();

    const missing = this.validateRequiredParams(params);
    if (missing.length) {
      this.errorMessage = `Report cannot load. Missing: ${missing.join(", ")}`;
      this.isLoading$.next(false);
      console.error("Missing required parameters:", missing);
      return;
    }
    this.errorMessage = "";

    const fullUrl = this.buildReportUrl(params);
    if (!fullUrl) {
      this.isLoading$.next(false);
      return;
    }

    this.isUrlSafeLoad = false;
    this.current_url = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
    setTimeout(() => {
      this.isLoading$.next(false);
    }, 100);
  }

  private setupResourceReportListenersIfNeeded() {
    const key = (this.data.category?.report?.reportUrl || "").toLowerCase();
    if (
      key.includes("rptresourceremainderconsumption") ||
      key.includes("rptresourceconsumption") ||
      key.includes("rptresourcestatus") ||
      key.includes("rptpolicykpistatus") ||
      key.includes("rptresourcepolicy")
    ) {
      this.isResourceRemainderConsumption$.next(true);

      // Store the listener so we can remove it later
      this.messageListener = (event: MessageEvent) => {
        if (event.data?.event === "InvokeReportActionEvent" && event.data?.message != undefined) {
          this.isUrlsReturnLength.next(true);
        }

        if ((event.data?.type === "carbon-kpi" || event.data?.event === "CarbonKPI") && this.isCarbonReport) {
          this.handleCarbonKPI(event.data);
        }
      };

      window.addEventListener("message", this.messageListener);
    }
  }

  private messageListener: any;

  ngOnDestroy(): void {
    this.latestSub?.unsubscribe();
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
    }
    this.carbonKpiData = null;
  }

  carbonKpiData: any = null;

  handleCarbonKPI(data: any) {
    const value = +data.value;
    const unit = (data.unit || "").toLowerCase();

    // Only show if it looks like a carbon/emissions metric and we are on the carbon report
    if (!this.isCarbonReport || !unit.includes("kg") || (!unit.includes("co2") && !unit.includes("carbon"))) {
      return;
    }

    const trees = Math.ceil(value / 22);

    this.carbonKpiData = {
      month: data.month,
      value: value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      unit: data.unit,
      treeEquivalent: trees,
      formula: `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${data.unit || 'kg CO₂'} ÷ 22 = ${(value / 22).toFixed(2)} → ${trees}`
    };
    this.cdr.detectChanges();
  }

  closeCarbonKpi() {
    this.carbonKpiData = null;
    this.cdr.detectChanges();
  }

  changeChartType(chartType: IChart) {
    this.chartType = chartType;
    this.carbonKpiData = null; // Clear KPI on chart change
    this.updateReportUrl();
  }

  changeGroup(group: GroupVM) {
    this.selectedGroup = group;
    this.carbonKpiData = null; // Clear KPI on group change
    // A different meter can stop on a different day. Until the user picks dates, follow it.
    this.openOnLatestReading();
    this.updateReportUrl();
  }

  changeShowLabel() {
    this.updateReportUrl();
  }

  changeToolbar() {
    this.updateReportUrl();
  }

  // ---- Opening window -----------------------------------------------------------------------

  /**
   * A native chart opens on the 16 weeks up to the newest reading behind it, in weekly bars.
   *
   * It used to open on the category's DefaultFormat counted back from today. That varied from
   * category to category, and for any meter that had stopped reporting, a site on bills a month
   * behind, or dev's seeded data, it opened on an empty chart. The dates and the period buttons
   * are unchanged: this is only where it starts.
   *
   * Classic SSRS charts and categories that open in Compare keep their own defaults.
   */
  static readonly OPENING_WEEKS = 16;

  /** False while the opening window is being looked up, so the chart does not draw today's first. */
  nativeWindowReady = true;

  /** Says why the chart opened on an older window than today. Null when it ends today. */
  latestReadingNote: string | null = null;

  /** Once the user picks a date, the window is theirs and a group change no longer moves it. */
  private datesChosen = false;

  /** Likewise a period the user picked: a change of meter used to put Week back. */
  private periodChosen = false;

  /** Said under the dates when the ones entered cannot make a window. */
  dateMessage: string | null = null;

  private latestSub?: Subscription;

  private openOnLatestReading(): void {
    if (!this.useNativeChart || this.isCompare || this.datesChosen) return;

    // Weekly bars, where the category offers weeks at all, unless the user has picked a period. A
    // category that offers days and not weeks opens on its own days (see openingStart).
    if (!this.periodChosen && (!this.aPeriodList.length || this.aPeriodList.includes('Week'))) this.selectedPeriod = 'Week';

    this.nativeWindowReady = false;
    this.latestSub?.unsubscribe();
    this.latestSub = this.userMenuService
      .getLatestReading$(this.nativeSiteId, this.nativeReadingTypeId, this.nativeGroupType, this.nativeGroupId)
      .subscribe({
        next: r => this.applyOpeningWindow(r?.latest ? moment(r.latest).toDate() : null),
        // No answer is no worse than before: the window ends today.
        error: () => this.applyOpeningWindow(null)
      });
  }

  private applyOpeningWindow(latest: Date | null): void {
    const stale = !!latest && latest < moment().startOf('day').toDate();
    const end = stale ? latest! : new Date();

    this.endDate = end;
    this.startDate = this.openingStart(end);
    this.startDateForIframe = this.changeStartDateWithPeriod(this.startDate);
    this.endDateForIframe = this.changeEndDateWithPeriod(this.endDate);

    this.latestReadingNote = stale
      ? `Last reading found: ${moment(latest).format('D MMMM YYYY')}. Chart filtered to latest available data.`
      : null;

    this.nativeWindowReady = true;
    this.updateReportUrl();
    this.cdr.markForCheck();
  }

  /**
   * Where the opening window starts: 16 weeks back, in weekly bars, where the category offers weeks.
   * A category that offers only months, quarters or years opens on its own DefaultFormat's count of
   * them ("3YB" is 3 years), in whole periods: 16 weeks of a Year chart was one bar for part of a
   * year, and with ten fuel policies stacked in it unreadable (TLI Group, 12/10/26).
   */
  private openingStart(end: Date): Date {
    const period = this.periodLetter;
    if (period === 'w') return subtractPeriods(end, ModalReportsComponent.OPENING_WEEKS, 'w');

    // Days, months, quarters and years open on the category's own DefaultFormat count of them ("7DB"
    // is 7 days, "3YB" 3 years). A day-only category used to fall through to 16 weeks of days, 112
    // bars where its SSRS report had opened on 7.
    const told = /(\d+)([DWMQY])/.exec(this.data.category.defaultFormat || '');
    const fallback: Record<string, number> = { d: 7, m: 12, q: 4, y: 3 };
    const count = told && told[2].toLowerCase() === period ? Math.max(1, +told[1]) : (fallback[period] ?? 7);
    return startOfPeriod(subtractPeriods(end, count - 1, period), period);
  }

  /** The selected period as the chart's one letter. */
  private get periodLetter(): Period {
    const p = (this.selectedPeriod || 'Day')[0].toLowerCase();
    return (['d', 'w', 'm', 'q', 'y'].includes(p) ? p : 'd') as Period;
  }

  /** A date typed or picked by the user. The note no longer applies to what is on screen. */
  onDateChosen(which: 'start' | 'end'): void {
    this.datesChosen = true;
    this.latestReadingNote = null;
    // A lookup still on its way (a meter just changed) would put the opening window back over the
    // dates the user has just typed, so it is dropped and the chart draws theirs.
    this.latestSub?.unsubscribe();
    this.nativeWindowReady = true;
    if (which === 'start') this.changeStartDate(); else this.changeEndDate();
  }

  changeStartDate() {
    // A date that did not parse keeps the one before it. It used to become today, which with an end
    // before today made a window that ended before it began.
    this.startDate = this.asDate(this.startDate) ?? this.nativeStart ?? new Date();
    this.startDateForIframe = this.changeStartDateWithPeriod(this.startDate);

    // if (!this.isCompare && this.selectedPeriod === "Day" && this.startDateForIframe) {
    //   this.endDate = new Date(this.startDateForIframe);
    // }

    this.changeEndDate();
  }

  changeEndDate() {
    this.endDate = this.asDate(this.endDate) ?? this.nativeEnd ?? new Date();

    if (this.isCompare) {
      this.endDateForIframe = this.changeStartDateWithPeriod(this.endDate);
    } else {
      this.endDateForIframe = this.changeEndDateWithPeriod(this.endDate);
    }

    this.updateReportUrl();
  }

  changeCompare() {
    this.carbonKpiData = null; // Clear KPI on compare toggle
    this.changePeriod(this.selectedPeriod!);
  }

  changePeriod(period: string) {
    this.selectedPeriod = period;
    this.periodChosen = true;
    this.carbonKpiData = null; // Clear KPI on period change
    this.changeStartDate();
    this.changeEndDate();
    this.updateReportUrl();
  }

  /**
   * The start of the period holding a date, and the last second of it, by moment (periods.ts). The
   * hand-written versions moved a Sunday into the following week and took 31 January's month to end
   * on 28 February, and the SSRS URLs and Compare both read them.
   */
  private changeStartDateWithPeriod(date?: any): Date {
    return startOfPeriod(this.asDate(date) ?? new Date(), this.periodLetter);
  }

  private changeEndDateWithPeriod(date?: any): Date {
    return endOfPeriod(this.asDate(date) ?? new Date(), this.periodLetter);
  }

  private createPeriodList() {
    this.aPeriodList = [];
    this.selectedPeriod = undefined;

    let showFormat = this.data.category.showFormat || "";
    if (showFormat.includes("M") || showFormat.includes("Y")) {
      if (!showFormat.includes("Q")) {
        const mIndex = showFormat.indexOf("M");
        if (mIndex !== -1) {
          showFormat = showFormat.slice(0, mIndex + 1) + "Q" + showFormat.slice(mIndex + 1);
        } else {
          showFormat += "Q";
        }
      }
    }

    for (const [key, value] of Object.entries(this.periodListType)) {
      if (showFormat.includes(key)) this.aPeriodList.push(value);
      if (this.data.category.defaultFormat?.includes(key)) this.selectedPeriod = value;
    }

    this.isShowDatepicker = this.aPeriodList.length !== 0;
  }

  private createDefaultData() {
    const sd = this.asDate(this.startDate);
    if (!sd) {
      this.startDate = new Date();
      this.startDate.setHours(0, 0, 0, 0);

      if (!this.selectedPeriod) return;

      // Counted back by moment, which clamps at a month's end: one month before 31 October is
      // 30 September. setMonth made it 1 October, the current month, so Compare's left period was the
      // same as its right.
      const told = /(\d+)([DWMQY])/.exec(this.data.category.defaultFormat || '');
      const count = told && told[2].toLowerCase() === this.periodLetter ? +told[1] || 1 : 1;
      this.startDate = subtractPeriods(this.startDate, count, this.periodLetter);
    }

    const ed = this.asDate(this.endDate);
    if (!ed) this.endDate = new Date();

    if (!this.startDateForIframe) this.startDateForIframe = this.changeStartDateWithPeriod(this.startDate);
    if (!this.endDateForIframe) {
      this.endDateForIframe = this.isCompare
        ? this.changeStartDateWithPeriod(this.endDate)
        : this.changeEndDateWithPeriod(this.endDate);
    }
  }

  private createChartTypeList() {
    this.chartTypeList = [];

    if (this.data.category.showFormat?.includes("B")) {
      this.chartTypeList.push({ name: "Bar", iconName: 'bar_chart', tooltip: "Show Bar Chart Report" });
    }
    if (this.data.category.showFormat?.includes("P")) {
      this.chartTypeList.push({ name: "Pie", iconName: 'pie_chart', tooltip: "Show Breakdown Pie Chart" });
    }

    if (this.chartTypeList.length) {
      if (this.data.category.defaultFormat?.includes('B')) {
        this.chartType = this.chartTypeList.find(c => c.name === 'Bar')!;
      } else if (this.data.category.defaultFormat?.includes('P')) {
        this.chartType = this.chartTypeList.find(c => c.name === 'Pie')!;
      }
    }
  }

  private createCompare() {
    this.isShowCompare = this.data.category.showFormat?.includes("C") ?? false;
    this.isCompare = !!this.data.category.defaultFormat?.includes('C');
  }

  /**
   * We DO NOT use HttpParams for SSRS URLs because:
   * - it encodes ":" into %3A (and sometimes you ended up with %253A)
   * - you observed SSRS works with raw ":" timestamps
   */
  private createReportParams(): Record<string, string> {
    const reportUrl = this.data.category?.report?.reportUrl || "";
    const reportKey = reportUrl.toLowerCase();
    const isAlarm = reportKey.includes("rptpolicykpistatus");

    const portalId = localStorage.getItem('portalId') ?? '';
    const siteId = localStorage.getItem('siteId') ?? '';

    const sg: any = this.selectedGroup;

    const params: Record<string, string> = {
      aPortalID: portalId,
      aSiteID: siteId,
      aShowLabel: String(this.isShowLabel),
    };

    if (this.chartType?.name) {
      params["aChartType"] = this.chartType.name;
    }

    // group selector (if valid)
    const hasValidGroup = !!sg && !!sg.type && sg.id !== undefined && sg.id !== null;
    if (hasValidGroup) {
      params[String(sg.type)] = String(sg.id);
    }

    if (isAlarm) {
      // Must be "day"
      params["aPeriod"] = "day";
      params["aReadingTypeID"] = String(sg?.readingTypeId ?? 1);

      // Policy ID optional (include if we can resolve dynamically)
      const policyId = this.resolvePolicyIdFromAnywhere();
      if (policyId) params["aPolicyID"] = policyId;

      // Your observation: report returns data only when both are end-of-day
      const baseDay = this.startDateForIframe ?? this.startDate ?? new Date();
      const dEnd = new Date(baseDay);
      dEnd.setHours(23, 59, 59, 0);

      params["aDateLeft"] = this.formatAlarmDate(dEnd);
      params["aDateRight"] = this.formatAlarmDate(dEnd);
    } else {
      // normal reports
      if (this.selectedPeriod) {
        params["aPeriod"] = (this.isCompare ? "1" : "") + this.selectedPeriod[0].toLowerCase();
      }

      if (sg?.readingTypeId != null) params["aReadingTypeID"] = String(sg.readingTypeId);
      if (sg?.policyId != null) params["aPolicyID"] = String(sg.policyId);

      if (this.startDateForIframe) params["aDateLeft"] = this.formatNormalDate(this.startDateForIframe);
      if (this.endDateForIframe) params["aDateRight"] = this.formatNormalDate(this.endDateForIframe);
    }

    return params;
  }

  private validateRequiredParams(params: Record<string, string>): string[] {
    const reportKey = (this.data.category.report?.reportUrl || "").toLowerCase();
    const isAlarm = reportKey.includes("rptpolicykpistatus");

    // Alarm report: DO NOT require aPolicyID on client (you confirmed it can still work)
    const required = isAlarm
      ? ["aPortalID", "aSiteID", "aReadingTypeID", "aPeriod", "aDateLeft", "aDateRight"]
      : ["aPortalID", "aSiteID", "aPeriod", "aDateLeft", "aDateRight"];

    return required.filter(k => !params[k] || String(params[k]).trim() === "");
  }

  private buildReportUrl(params: Record<string, string>): string | null {
    const reportUrl = this.data.category?.report?.reportUrl;
    if (!reportUrl) return null;

    const reportName = this.isCompare
      ? (reportUrl === "rptResourceRemainderConsumption"
        ? "rptResourceRemainderComparison"
        : "rptResourceCompare")
      : reportUrl;

    const base0 = this.url.endsWith('/') ? this.url : (this.url + '/');
    const base = base0.replace(/\/DCS\.\/*$/i, "/DCS.Reports/");

    const isAlarm = reportUrl.toLowerCase().includes("rptpolicykpistatus");

    const renderBits = `&rs:Command=Render&rc:Parameters=false&rc:Toolbar=${this.isToolbarActive}`;

    const qs = this.toSsrsQueryString(params);
    const fullUrl = `${base}${reportName}${renderBits}&${qs}`;

    return fullUrl;
  }

  /**
   * SSRS-friendly encoding:
   * - encode dangerous characters
   * - KEEP ":" unencoded (you observed SSRS works with raw timestamps)
   * - KEEP "T" as-is
   */
  private toSsrsQueryString(params: Record<string, string>): string {
    const enc = (v: string) => encodeURIComponent(v)
      .replace(/%3A/gi, ":"); // keep colon

    return Object.keys(params)
      .map(k => `${enc(k)}=${enc(String(params[k]))}`)
      .join("&");
  }

  returnLastUrl() {
    this.carbonKpiData = null; // Clear KPI on return
    this.updateReportUrl();
    this.isUrlsReturnLength.next(false);
  }

  private asDate(value: any): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }

  private resolvePolicyIdFromAnywhere(): string | null {
    // Try group first
    const sg: any = this.selectedGroup;
    const fromGroup = sg?.policyId ?? sg?.PolicyID ?? sg?.policyID ?? null;
    if (fromGroup != null && String(fromGroup).trim() !== "") return String(fromGroup).trim();

    // Try storage keys
    const siteId = localStorage.getItem("siteId") || "";
    const keys = [
      `lastPolicyId_site_${siteId}`,
      "lastPolicyId",
      "aPolicyID",
      "policyId",
      "policyID",
      "PolicyID",
      "selectedPolicyId",
      "selectedPolicyID",
    ];

    for (const k of keys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v && v.trim() !== "") return v.trim();
    }

    return null;
  }

  private formatAlarmDate(date: Date): string {
    // MUST be: 2026-01-13T23:59:59
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  }


  // ---- Native Mains/Solar PV chart (feature-flagged) -------------------------------------------
  // The SSRS iframe re-renders server side on every parameter change, so it can never drive a live
  // control such as the solar multiplier. This swaps in an Angular chart fed by /resource-series
  // for the one report that needs it, and leaves every other category on the iframe.

  /** Seeded from the environment, then switchable in the UI so this can be compared side by side. */
  nativeChartEnabled: boolean = environment.nativeResourceChart;

  /**
   * The tickbox is worded the other way round: the native chart is what people get, and the old
   * SSRS report is the opt-out. Same single flag underneath.
   */
  get useClassicChart(): boolean { return !this.nativeChartEnabled; }
  set useClassicChart(value: boolean) { this.nativeChartEnabled = !value; }

  /**
   * The reports the /resource-series endpoint can answer for.
   *
   * Three procs back all of these and the endpoint reads them through one path. Counted on
   * production 21/09/2026 they are 287 of the 301 FPCategories. The other 14 - the temperature,
   * relay, boiler, power factor and service reports - are each a different shape and stay on SSRS.
   */
  private static readonly NATIVE_REPORTS = [
    'rptresourceremainderconsumption',
    'rptresourcepolicy',
    'rptresourcepolicybreakdown',
    'rptresourceconsumption',
    'rptresourcestatus'
  ];

  /**
   * Whether the endpoint can answer for this category at all.
   *
   * Compare included: it swaps the report for rptResourceRemainderComparison or rptResourceCompare,
   * and /resource-comparison runs the same procs, so the chart draws Compare natively too.
   */
  get isNativeReport(): boolean {
    const reportUrl = (this.data.category?.report?.reportUrl || '').toLowerCase();
    return ModalReportsComponent.NATIVE_REPORTS.includes(reportUrl);
  }

  /**
   * Compare's two period starts, as the SSRS Compare reports take them: the start of the period
   * holding the start date, and the start of the one holding the end date. startDateForIframe and
   * endDateForIframe are already snapped that way while Compare is ticked (see changeEndDate).
   */
  get nativeCompareLeft(): string {
    return this.startDateForIframe ? moment(this.startDateForIframe).format('YYYY-MM-DD HH:mm:ss') : '';
  }

  get nativeCompareRight(): string {
    if (!this.endDateForIframe) return '';
    // Both dates in the same period would compare a period with itself, and the proc then returns one
    // side only. The right one moves on a period, as the next one along is what anyone means.
    const left = this.startDateForIframe ? startOfPeriod(this.startDateForIframe, this.periodLetter).getTime() : NaN;
    const right = startOfPeriod(this.endDateForIframe, this.periodLetter);
    return stamp(right.getTime() === left ? nextPeriodStart(right, this.periodLetter) : right);
  }

  /** The bar chart takes the report, group type and group id too, so it answers for the same set. */
  get canUseNativeChart(): boolean {
    return this.isNativeReport;
  }

  /** The pie takes all three, so it can answer for every report the endpoint serves. */
  get canUseNativePie(): boolean {
    return this.isNativeReport;
  }

  /** The category's own report, passed through so the endpoint knows which proc to run. */
  get nativeReportUrl(): string {
    return this.data.category?.report?.reportUrl || '';
  }

  /**
   * The selected group's own parameter name - "aLogicalGroupID", "aPolicyID" or "aPolicyGroupID".
   * The same value the SSRS URL binds the group's id to, passed through rather than re-derived.
   */
  get nativeGroupType(): string | undefined {
    return (this.selectedGroup as any)?.type || undefined;
  }

  /** The category and its selected group, for the chart's export file names and print title. */
  get nativeChartTitle(): string {
    const group = (this.selectedGroup as any)?.name;
    const category = this.data.category?.categoryName || '';
    return group && group !== category ? `${category}: ${group}` : category;
  }

  get nativeGroupId(): number | undefined {
    const id = (this.selectedGroup as any)?.id;
    return id === undefined || id === null ? undefined : Number(id);
  }

  /** Bar or Pie, on the native path. Bar whenever the category never offered a pie. */
  get isNativePie(): boolean {
    // Compare draws its own pie for each period beside the bars.
    return this.canUseNativePie && this.nativeChartEnabled && this.chartType?.name === 'Pie' && !this.isCompare;
  }

  /**
   * The what-if panel's open state lives here rather than in the chart, because the button that
   * opens it sits in this dialog's header beside the H and T tickboxes.
   */
  showWhatIf = false;

  /**
   * Whether the chart has anything to scale. Reported by the chart once its series arrive - the
   * header cannot work that out on its own, and a button that opens an empty panel is worse than
   * no button.
   */
  whatIfAvailable = false;

  get useNativeChart(): boolean {
    return this.canUseNativeChart && this.nativeChartEnabled;
  }

  /**
   * Scopes the layout the native chart needs (see the .scss), so an SSRS report in this dialog is
   * laid out exactly as in production.
   */
  @HostBinding('class.native-dialog') get nativeDialog(): boolean {
    return this.useNativeChart;
  }

  get nativeSiteId(): number {
    return Number(localStorage.getItem('siteId') ?? 0);
  }

  get nativeReadingTypeId(): number {
    return Number((this.selectedGroup as any)?.readingTypeId ?? 0);
  }

  get nativePeriod(): string {
    return this.selectedPeriod ? this.selectedPeriod[0].toLowerCase() : 'd';
  }

  /**
   * The raw date the user set, not startDateForIframe. That field gets snapped to the selected
   * period's own calendar boundary (see changeStartDateWithPeriod), which is correct for the legacy
   * SSRS iframe, where a period button means "jump to this one period". On the native chart a
   * period button means "bucket this same range by day, week or month" instead, so reading the
   * snapped field was collapsing a custom two-year range down to a single month every time the
   * period changed. Reported live from the 11/09/26 demo as "the date range resets on every change".
   *
   * Taken back to the start of its own week, month, quarter or year, so the first bar is a whole
   * one. From the raw date, a range starting Sunday 23 August drew a bar labelled "Aug 17" holding
   * that Sunday alone (Jayden, 13/10/26); the SSRS report started on the period's first day too.
   * Weeks start on Monday, as the procs count them (SET DATEFIRST 1).
   */
  get nativeDateLeft(): string {
    return this.nativeStart ? stamp(startOfPeriod(this.nativeStart, this.periodLetter)) : '';
  }

  /**
   * The end of the chosen day, as SSRS sent it (changeEndDateWithPeriod's 23:59:59). The procs take
   * readings strictly before this, and the picker hands back the day's midnight, so sending that as
   * it was left the end date itself out of the chart: a range ending 30 September stopped at the 29th,
   * and a chart opened on a meter's last reading lost that reading.
   */
  get nativeDateRight(): string {
    // The end of the period holding the end date, as SSRS sent it (changeEndDateWithPeriod), so the
    // last bar is a whole week, month or year, not the part of one up to the date picked.
    return this.nativeEnd ? stamp(endOfPeriod(this.nativeEnd, this.periodLetter)) : '';
  }

  /**
   * The window the native chart is showing, taken from the date fields only once a date is chosen
   * (updateReportUrl), never while one is being typed. The getters above used to read the fields'
   * own models, so every keystroke that parsed was a new chart request: typing "1-Feb-2025" passed
   * through "1-Feb-202", which is the year 202, and the API's SQL refused it with "SqlDateTime
   * overflow" (TLI Group's "Fuel in Litres !", production, 12/10/26). A date outside 1900 - 2100 is
   * a typing slip, not a window, so it is not taken and the chart keeps the last one it had.
   */
  private nativeStart: Date | null = null;
  private nativeEnd: Date | null = null;

  private commitNativeDates(): void {
    const ok = (d: Date | undefined): d is Date => !!d && d.getFullYear() >= 1900 && d.getFullYear() <= 2100;
    const start = this.asDate(this.startDate);
    const end = this.asDate(this.endDate);

    // A start after the end makes no window: it used to be sent anyway and drew an empty chart with
    // nothing to say why. Compare is two starts, so either order is a comparison.
    if (!this.isCompare && ok(start) && ok(end) && startOfPeriod(start, 'd') > end) {
      this.dateMessage = 'The start date is after the end date.';
      return;
    }
    this.dateMessage = null;
    if (ok(start)) this.nativeStart = start;
    if (ok(end)) this.nativeEnd = end;
  }

  private formatNormalDate(date: Date): string {
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
  }

  getImageUrl(imageName: string) {
    // Your 404 is because your HTML is likely passing strings with spaces like "assets / images / ..."
    // This method expects a normal file name like "22-PolicyIndicator.png"
    const clean = (imageName || "").trim();
    const svgImageName = clean.replace(".png", ".svg");
    return `assets/images/svg/Items/${svgImageName}`;
  }

  onNoClick() {
    this.dialogRef.close();
  }
}

interface IChart {
  name: string;
  iconName: string;
  tooltip: string;
}
